import { Typography, Box } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { FC, useEffect, ReactNode } from 'react';
import axios from 'axios';

import { uploadManager } from './uploadManager';

import { uploadFileToS3 } from '@lendica/api';

export interface DropzoneFile extends Partial<File> {
    path?: string;
    progress?: number;
    key?: string;
    error?: string;
}

export interface Accept {
    [key: string]: string[];
}

export interface DropzoneProps {
    id?: string;
    label?: string;
    helperText?: string;
    value: DropzoneFile[];
    onChange(files: DropzoneFile[]): void;
    onDragActive?(value: boolean): void;
    onDrop?(files: DropzoneFile[]): void;
    onUploadFailed?(error: Error): void;
    onFileRejection?(value: boolean): void;
    api: {
        presignedUrl: string;
        presignedUrlParams?: {};
        getObjectKey?(fileName: string): string;
    };
    accept?: Accept | undefined;
    acceptHelper?: string;
    multiple?: boolean;
    showDragActive?: boolean;
    styles?: object;
    helperComponent?: ReactNode;
    maxFiles?: number; // Maximum number of files
    maxSize?: number; // Maximum file size in bytes
    minSize?: number; // Minimum file size in bytes
}

export const Dropzone: FC<DropzoneProps> = ({
    id = '',
    label,
    value,
    onChange,
    onDrop,
    onDragActive = () => {},
    onUploadFailed = () => {},
    onFileRejection = () => {},
    api: { presignedUrl, presignedUrlParams = {}, getObjectKey },
    accept,
    acceptHelper,
    multiple,
    showDragActive = false,
    styles = {},
    helperComponent,
    maxFiles,
    maxSize,
    minSize,
}) => {
    // workaround to react on progress changes
    // field.value can't be used inside onDrop since might be referencing an old value
    useEffect(() => {
        if (uploadManager.size !== 0) {
            const timeout = setTimeout(() => {
                onChange([...(value ?? [])]);
            }, 1000);
            return () => clearTimeout(timeout);
        }
    }, [value]);

    const { getRootProps, getInputProps, fileRejections, isDragActive } = useDropzone({
        accept,
        multiple,
        maxFiles,
        maxSize,
        minSize,
        onDrop: async files => {
            const filesWithKey = files.map(file => {
                return Object.assign(file, {
                    objectKey: getObjectKey ? getObjectKey(file.name) : file.name,
                });
            });

            onDrop?.(filesWithKey);
            const fileMap = new Map();

            const newFiles = filesWithKey.map(file => {
                const newFile = {
                    path: file.name,
                    progress: 0,
                };
                fileMap.set(file, { file: newFile });
                const source = axios.CancelToken.source();
                uploadManager.set(newFile, source);
                (async function () {
                    try {
                        const key = await uploadFileToS3(
                            file,
                            {
                                presignedUrl,
                                presignedUrlParams,
                                getObjectKey,
                            },
                            source,
                            ({ progress }) => {
                                fileMap.get(file).file.progress = progress;
                            }
                        );

                        fileMap.get(file).file.key = key;
                        uploadManager.delete(newFile);
                    } catch (e: any) {
                        onUploadFailed(e);

                        if (!axios.isCancel(e)) {
                            fileMap.get(file).file.error = true;
                        }
                        uploadManager.delete(newFile);
                    }
                })();

                return newFile;
            });

            onChange([...newFiles, ...(value ?? [])]);
        },
    });

    useEffect(() => {
        onDragActive(isDragActive);
    }, [isDragActive]);

    useEffect(() => {
        if (fileRejections.length > 0) {
            // Group errors by type to handle consolidation
            const errorGroups: { [key: string]: any[] } = {};

            fileRejections.forEach(rejection => {
                rejection.errors.forEach(error => {
                    if (!errorGroups[error.code]) {
                        errorGroups[error.code] = [];
                    }
                    errorGroups[error.code].push({ error, file: rejection.file });
                });
            });

            // Generate consolidated error messages
            const errorMessages: string[] = [];

            Object.entries(errorGroups).forEach(([errorCode, errorItems]) => {
                switch (errorCode) {
                    case 'too-many-files':
                        // Consolidate to single message regardless of how many files
                        errorMessages.push(`Cannot upload more than ${maxFiles} files at once`);
                        break;
                    case 'file-too-large':
                        // Individual message for each oversized file
                        errorItems.forEach(({ file }) => {
                            errorMessages.push(
                                `File "${file.name}" is too large. Maximum size is ${
                                    maxSize ? Math.round(maxSize / 1024 / 1024) : 10
                                }MB`
                            );
                        });
                        break;
                    case 'file-too-small':
                        // Individual message for each undersized file
                        errorItems.forEach(({ file }) => {
                            errorMessages.push(`File "${file.name}" is too small`);
                        });
                        break;
                    case 'file-invalid-type':
                        // Individual message for each invalid file type
                        errorItems.forEach(({ file }) => {
                            errorMessages.push(`File "${file.name}" is not a supported file type`);
                        });
                        break;
                    default:
                        // Individual message for other error types
                        errorItems.forEach(({ error }) => {
                            errorMessages.push(error.message);
                        });
                        break;
                }
            });

            // Call onUploadFailed with detailed error message
            const errorMessage = errorMessages.join('. ');
            onUploadFailed?.(new Error(errorMessage));

            console.log('[Dropzone] Files rejected:', fileRejections);
        }

        onFileRejection(!!fileRejections.length);
    }, [fileRejections, maxFiles, maxSize, onUploadFailed]);

    return (
        <Box
            {...getRootProps()}
            sx={{
                height: 210,
                backgroundColor: '#F2F2F8',
                border: '1px dashed #D7D7E0',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                ...styles,
            }}
        >
            <input id={`dropzone-input-appl-${id}`} {...getInputProps()} />
            {showDragActive && isDragActive ? (
                <Typography
                    color="text.disabled"
                    variant="subtitle1"
                    sx={{ fontSize: 24, color: '#8C8CA1' }}
                >
                    Drag and drop here
                </Typography>
            ) : helperComponent ? (
                helperComponent
            ) : (
                <>
                    {label && (
                        <Typography color="primary" variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                            {label}
                        </Typography>
                    )}
                    <Typography color="text.disabled" variant="subtitle1">
                        Drag and drop or click to upload files.
                    </Typography>
                    {acceptHelper && (
                        <Typography color="text.disabled" variant="subtitle1">
                            {acceptHelper}
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );
};
